import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Typography, Button, CircularProgress } from '@mui/joy'
import { ArrowLeft as ArrowBack } from '@phosphor-icons/react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { DocumentForm } from '../components/billing'
import { invoicesApi, taxRatesApi } from '../lib/billingApi'
import { apiClient } from '../lib/api'
import type { TaxRate } from '../types/billing'

export default function InvoiceCreate() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Job Detail's "Create Invoice" opens /billing/invoices/new?job=<id>.
  const jobId = searchParams.get('job')

  // Estimate -> Invoice conversion is server-side
  // (POST /api/billing/estimates/<id>/convert_to_invoice/). A second path used
  // to rebuild the invoice in the browser from location.state and lost the
  // discount, the overhead/profit flags and every line's real item_type and
  // taxability. Its only caller is gone; nothing should reinstate it here.

  // Fetch customers
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['contacts'],
    queryFn: async () => {
      const response = await apiClient.getContacts({ page_size: 1000 })
      return response.data
    }
  })

  // Fetch jobs (include completed — invoices are often tied after job close-out)
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['jobs', { includeCompleted: true, pageSize: 1000 }],
    queryFn: async () => {
      const response = await apiClient.getJobs(undefined, true, 1000)
      return response.data
    }
  })

  // Fetch tax rates
  const { data: taxRates = [], isLoading: taxRatesLoading } = useQuery<TaxRate[]>({
    queryKey: ['taxRates'],
    queryFn: async () => {
      const response = await taxRatesApi.list()
      return response.results
    }
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => invoicesApi.create(data),
    onSuccess: async (invoice: any) => {
      toast.success('Invoice created successfully')

      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      navigate(`../${invoice.id}`, { relative: 'path' })
    },
    onError: (error: any) => {
      console.error('Create invoice error details:', error.response?.data)
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.line_items?.[0] ||
                          JSON.stringify(error.response?.data) ||
                          'Failed to create invoice'
      toast.error(errorMessage)
      setIsSubmitting(false)
    }
  })

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true)
    
    // Prepare the data for POST request
    const createData = {
      customer: data.customer,
      job: data.job || null,
      opportunity: data.opportunity || null,
      document_date: data.document_date,
      due_date: data.due_date || null,
      payment_terms: data.payment_terms,
      custom_payment_terms: data.custom_payment_terms || '',
      terms_and_conditions: data.terms_and_conditions || '',
      notes: data.notes || '',
      internal_notes: data.internal_notes || '',
      is_recurring: data.is_recurring || false,
      recurring_frequency: data.recurring_frequency || null,
      late_fee_percentage: data.late_fee_percentage || 0,
      discount_amount: data.discount_amount || 0,
      // Overhead and profit fields
      include_overhead: data.include_overhead || false,
      overhead_percentage: data.overhead_percentage || null,
      include_profit: data.include_profit || false,
      profit_percentage: data.profit_percentage || null,
      // Sent whole: the map that used to stand here dropped each line's notes,
      // bold flag, taxable_pct, catalog linkage and internal sub-items.
      line_items: data.line_items
    }

    try {
      await createMutation.mutateAsync(createData)
    } catch (error) {
      throw error // Keep the form draft and show server validation fields.
    }
  }

  const handleCancel = () => {
    navigate('..')
  }

  const isLoading = customersLoading || jobsLoading || taxRatesLoading

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  // customersData and jobsData are already the results arrays due to ApiClient extracting them
  const customers = customersData || []
  const jobs = jobsData || []

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          component={Link}
          to="/operations/billing/invoices"
          startDecorator={<ArrowBack />}
          variant="plain"
          size="sm"
          sx={{ mb: 2 }}
        >
          Back to Invoices
        </Button>
        
        <Typography level="h1">Create New Invoice</Typography>
      </Box>
      
      <DocumentForm
        documentType="invoice"
        customers={customers}
        jobs={jobs}
        taxRates={taxRates}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isSubmitting}
        initialJobId={jobId ? Number(jobId) : undefined}
      />
    </Box>
  )
}